import React, { FunctionComponent } from "react"
import { ApolloClient, ApolloProvider, InMemoryCache } from "@apollo/client"
import { onError } from "@apollo/client/link/error"
import { ApolloLink } from "apollo-link"
import { useTokens } from "./common/hooks/useTokens"
import { useHistory, useLocation } from "react-router"
import { useNotification } from "./common/hooks/useNotification"
import { BatchHttpLink } from "@apollo/client/link/batch-http"

export interface ApolloProviderDecoratorProps {
    children: React.ReactNode
}
enum API_ERROR_CODES {
    NOT_AUTHENTICATED = "UNAUTHENTICATED"
}

export const ApolloClientProvider: FunctionComponent<ApolloProviderDecoratorProps> = props => {
    const { getAccessToken, setAccessToken, setRefreshToken } = useTokens()
    const hist = useHistory()
    const loc = useLocation()
    const { notifyInfo } = useNotification()

    const customFetch = (uri: string, options: RequestInit) => {
        const body = JSON.parse(options.body as string)
        const operationName = Array.isArray(body)
            ? body.map(({ operationName }) => operationName).join(",")
            : body.operationName
        const apiUrl = `${window.location.protocol}//${window.location.host}/api/graphql?opname=${operationName}`
        return fetch(apiUrl, options)
    }

    const logout = () => {
        setAccessToken("")
        setRefreshToken("")
        hist.push("/login")
    }

    const errorLink = onError(({ graphQLErrors, networkError }) => {
        if (
            graphQLErrors?.some(err => err?.extensions?.code === API_ERROR_CODES.NOT_AUTHENTICATED)
        ) {
            logout()
        }
    })

    const httpLink = new BatchHttpLink({ fetch: customFetch })
    const authLink = new ApolloLink((operation, forward) => {
        const existingHeaders = operation.getContext().headers
        const accessToken = getAccessToken()
        operation.setContext({
            headers: {
                ...existingHeaders,
                authorization: accessToken ? `Bearer ${accessToken}` : null
            }
        })
        return forward(operation)
    })

    // httpLink must be last
    // @ts-ignore
    const links = authLink.concat(errorLink).concat(httpLink)

    const client = new ApolloClient({
        // @ts-ignore
        link: links,
        cache: new InMemoryCache()
    })
    return <ApolloProvider client={client} children={props.children} />
}
